import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { isEmpty, orderBy, times } from 'lodash'
import { CgAdd, CgImage } from 'react-icons/cg'

import { useAuth } from 'modules/Auth/hooks/useAuth'
import { useListenUserProfileQuery } from 'modules/Users/userApi'
import {
  useListenCouponsQuery,
  useListenLegacyProductPricingQuery,
  useListenOneTimeProductsQuery,
  useListenProductCurrencyQuery,
  useListenProductInfoLogoQuery,
  useListenProductPricingQuery,
  useListenStripeProductIdQuery,
  useSetProductPricesMutation,
} from '../productInfoApi'
import {
  useListenCoachOrgSlugQuery,
  useListenPublishedCheckoutPageQuery,
  useListenPublishedSalesPageQuery,
} from 'modules/PageBuilder/pageBuilderApi'

import { PAGEBUILDER_CHECKOUT_PAGE, PRICING, STRIPE_SETUP } from 'modules/App/components/Header'
import { createUID } from 'common/utils/createUID'

import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { FormRefsControlProvider } from 'common/components/RefsControl/FormRefsControl/context'
import { Skeleton } from 'common/components/Skeleton/Skeleton'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import CreateProductForm from '../components/CreateProductForm/CreateProductForm'
import ProductCard from '../components/ProductCard/ProductCard'
import { ShareLinkMenu } from '../components/ShareLinkMenu/ShareLinkMenu'
import { StatusPill } from '../components/StatusPill/StatusPill'
import { getStorefrontPagePath } from '../utils/shareLinks'
import { MEMBERSHIP_SHARE_GATE_MESSAGE } from '../constants/shareConstants'
import { getProductCurrency } from '../utils/utils'
import { getOneTimeProductSlugs } from '../utils/oneTimeProductUtils'

export default function Sell() {
  const { createAlert } = useAlert()
  const navigate = useNavigate()
  const { userId } = useAuth()

  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const [setProductPrices, { isLoading: isUpdatingProductPrices }] = useSetProductPricesMutation()

  const { data: profile } = useListenUserProfileQuery({ userId })
  const coachOrgId = profile?.coachOrgId || ''

  const { data: oneTimeProductsData } = useListenOneTimeProductsQuery({ coachOrgId })
  const oneTimeProductsLoading = oneTimeProductsData === undefined || oneTimeProductsData?.isLoading
  const sortedProducts = orderBy(oneTimeProductsData, (product) => product?.createdAt ?? 0, 'desc')

  const { data: legacyPlansData } = useListenLegacyProductPricingQuery({ coachOrgId })
  const legacyProductPlansLoading = legacyPlansData === undefined || legacyPlansData?.isLoading
  const legacyProductPlans = legacyProductPlansLoading || !legacyPlansData ? [] : legacyPlansData

  const { data: plansData } = useListenProductPricingQuery({ coachOrgId })
  const productPlansLoading = plansData === undefined || plansData?.isLoading
  const productPlans = productPlansLoading || !plansData ? {} : plansData

  const { data: coupons } = useListenCouponsQuery({ coachOrgId })
  const couponsLoading = coupons === undefined || coupons?.isLoading

  const { data: stripeProductId } = useListenStripeProductIdQuery({ coachOrgId })
  const stripeProductIdLoading = stripeProductId === undefined || stripeProductId?.isLoading

  const { data: currency } = useListenProductCurrencyQuery({ coachOrgId })
  const currencyLoading = currency === undefined || currency?.isLoading

  const { data: logo } = useListenProductInfoLogoQuery({ coachOrgId })
  const logoLoading = logo === undefined || logo?.isLoading

  const { data: coachOrgSlugData } = useListenCoachOrgSlugQuery({ coachOrgId })
  const coachOrgSlugLoading = coachOrgSlugData === undefined || coachOrgSlugData?.isLoading
  const coachOrgSlug = coachOrgSlugLoading ? '' : coachOrgSlugData || ''

  const { data: salesPageData } = useListenPublishedSalesPageQuery({ coachOrgId })
  const salesPageLoading = salesPageData === undefined || salesPageData?.isLoading
  const membershipPagePath = salesPageLoading ? '' : getStorefrontPagePath({ salesPage: salesPageData })
  // The membership storefront only loads once its checkout page is published
  const { data: checkoutPageData } = useListenPublishedCheckoutPageQuery({ coachOrgId })
  const checkoutPageLoading = checkoutPageData === undefined || checkoutPageData?.isLoading
  const membershipShareBlockedReason = isEmpty(checkoutPageData) ? MEMBERSHIP_SHARE_GATE_MESSAGE : null
  // Active only when people can actually subscribe: checkout published and a link handle set
  const isMembershipLive = !membershipShareBlockedReason && Boolean(coachOrgSlug)

  // The share menu sits inside the card, and its popup renders outside the card's DOM, so only
  // clicks on the card itself open Pricing.
  const onSubscriptionCardClick = (e) => {
    if (e.currentTarget.contains(e.target) && !e.target.closest?.('[data-share-trigger]')) navigate(PRICING)
  }

  const loading =
    legacyProductPlansLoading ||
    productPlansLoading ||
    couponsLoading ||
    oneTimeProductsLoading ||
    stripeProductIdLoading ||
    isUpdatingProductPrices ||
    currencyLoading ||
    logoLoading ||
    coachOrgSlugLoading ||
    salesPageLoading ||
    checkoutPageLoading

  useEffect(() => {
    // Migrate legacy array based plans to new object based plans
    if (!productPlansLoading && !legacyProductPlansLoading && isEmpty(productPlans) && !isEmpty(legacyProductPlans)) {
      let updatedPlans = {}
      legacyProductPlans.forEach((plan, index) => {
        const planId = createUID()
        updatedPlans[planId] = {
          ...plan,
          idx: plan?.idx || index,
          id: planId,
          stripe_plan_id: plan?.stripe_price_id || plan?.stripe_plan_id,
        }
      })
      setProductPrices({ coachOrgId, plans: updatedPlans })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productPlansLoading, legacyProductPlansLoading])

  useEffect(() => {
    if (!loading && !stripeProductId) {
      createAlert({ text: 'Please create Stripe product first', type: 'danger' })
      navigate(STRIPE_SETUP)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, stripeProductId])

  if (loading) {
    return (
      <div className='flex flex-col min-h-screen bg-offWhite px-4 md:px-16'>
        <div className='w-full max-w-[90rem] mx-auto mt-6 md:mt-12'>
          <div className='flex flex-col mb-12 md:mb-20'>
            <h3 className='font-bold text-3xl text-tBlack mb-6'>Subscriptions</h3>
            <div className='grid grid-cols-3 gap-6 mb-12'>
              {times(1, (idx) => (
                <Skeleton key={idx} className='rounded-xl min-h-[112px] border-2 border-gray-200' />
              ))}
            </div>
            <h3 className='font-bold text-3xl text-tBlack mb-6'>One-time Products</h3>
            <div className='grid grid-cols-3 gap-6 mb-12'>
              {times(6, (idx) => (
                <Skeleton key={idx} className='rounded-xl min-h-[112px] border-2 border-gray-200' />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col min-h-screen bg-offWhite px-4 md:px-16'>
      <div className='w-full max-w-[90rem] mx-auto mt-6 md:mt-12'>
        <div className='flex flex-col mb-12 md:mb-20'>
          <h3 className='font-bold text-3xl text-tBlack mb-6'>Subscriptions</h3>
          <div className='grid grid-cols-3 gap-6 mb-12'>
            {isEmpty(productPlans) ? (
              <button
                onClick={() => navigate(PRICING)}
                className='flex items-center justify-center bg-white p-4 rounded-xl hover:border-gray-300 cursor-pointer min-h-[112px] text-center border-2 border-gray-200'
              >
                <CgAdd className='w-6 h-6 text-tBlack' />
                <h4 className='font-bold text-tBlack capitalize ml-2'>Create Subscription Product</h4>
              </button>
            ) : (
              <button
                onClick={onSubscriptionCardClick}
                className='flex flex-col bg-white rounded-2xl cursor-pointer text-left border border-[#e6e8ec] transition-shadow hover:shadow-[0_8px_24px_rgba(28,34,64,0.08)]'
              >
                <div className='flex items-center gap-4 p-5'>
                  <div className='relative flex items-center justify-center w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden'>
                    {Boolean(logo) ? (
                      <img src={logo} alt='App Subscription' className='w-full h-full object-contain' />
                    ) : (
                      <div className='bg-tBlack absolute inset-0'>
                        <CgImage className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-50 w-7 h-7 z-10' />
                      </div>
                    )}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <h4 className='text-lg font-semibold text-[#1c2240] truncate'>App Subscription</h4>
                    <p className='text-base text-tGray-dark'>Manage pricing, landing page, checkout & more</p>
                  </div>
                </div>
                <div className='flex items-center justify-between gap-3 px-5 py-3 border-t border-[#eef0f3]'>
                  <StatusPill
                    label={isMembershipLive ? 'Active' : 'Draft'}
                    tone={isMembershipLive ? 'live' : 'draft'}
                  />
                  <ShareLinkMenu
                    tag='p'
                    coachSlug={coachOrgSlug}
                    pagePath={membershipPagePath}
                    blockedReason={membershipShareBlockedReason}
                    variant='button'
                    setupHref={PAGEBUILDER_CHECKOUT_PAGE}
                  />
                </div>
              </button>
            )}
          </div>
          <h3 className='flex items-baseline gap-2 font-bold text-3xl text-tBlack mb-6'>
            One-time Products
            {sortedProducts.length > 0 && (
              <span className='text-base font-semibold text-tGray-dark'>{sortedProducts.length}</span>
            )}
          </h3>
          <div className='grid grid-cols-3 gap-6 items-stretch'>
            <Dialog open={createDialogOpen} setOpen={setCreateDialogOpen}>
              <DialogTrigger className='flex items-center justify-center gap-3 rounded-2xl cursor-pointer min-h-[180px] text-center border-2 border-dashed border-[#d5d9e0] bg-[#f3f4f6] hover:border-[#2fb597] transition-colors'>
                <span className='flex items-center justify-center w-10 h-10 rounded-full bg-[#e8f6ef] text-[#1f7a55]'>
                  <CgAdd className='w-5 h-5' />
                </span>
                <span className='text-lg font-semibold text-tGray-dark'>Create one-time product</span>
              </DialogTrigger>
              <DialogContent header='Create product'>
                <FormRefsControlProvider>
                  <CreateProductForm
                    coachOrgId={coachOrgId}
                    currency={getProductCurrency({ defaultCurrency: currency })}
                    oneTimeProductsData={oneTimeProductsData}
                  />
                </FormRefsControlProvider>
              </DialogContent>
            </Dialog>
            {sortedProducts.map((product) => (
              <ProductCard
                key={product?.id}
                coachOrgId={coachOrgId}
                product={product}
                currency={getProductCurrency({ productCurrency: product?.currency, defaultCurrency: currency })}
                coupons={coupons}
                oneTimeProductSlugs={getOneTimeProductSlugs({ oneTimeProductsData, productId: product?.id })}
                coachOrgSlug={coachOrgSlug}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
